import { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { useLiveData, useActionWithRefresh } from "../hooks/useLiveData";
import {
  getPendingReviews,
  getReviewedCommits,
  getCommitDiff,
  canReviewCommit,
  approveCommit,
  requestChanges,
  type FileDiff,
} from "../services/reviews";
import type { Commit } from "../types";

const reviewStatusColors: Record<string, string> = {
  pending: "badge-warning",
  approved: "badge-success",
  "changes-requested": "badge-danger",
};

export default function CodeReview() {
  const { can, session } = useAuth();
  const [selectedCommit, setSelectedCommit] = useState<Commit | null>(null);
  const [diffs, setDiffs] = useState<FileDiff[]>([]);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [reviewComment, setReviewComment] = useState("");
  const [tab, setTab] = useState<"pending" | "reviewed">("pending");
  const [canReview, setCanReview] = useState(false);

  const pending = useLiveData<Commit[]>({ fetcher: getPendingReviews, interval: 15000 });
  const reviewed = useLiveData<Commit[]>({ fetcher: getReviewedCommits, interval: 30000 });

  const refreshAll = async () => {
    await Promise.all([pending.refresh(), reviewed.refresh()]);
  };
  const { busy, actionError, execute, clearError } = useActionWithRefresh(refreshAll);

  async function handleSelectCommit(commit: Commit) {
    setSelectedCommit(commit);
    setDiffs([]);
    setLoadingDiff(true);
    setReviewComment("");
    try {
      const [diffResult, canReviewResult] = await Promise.all([
        getCommitDiff(commit.commitId),
        canReviewCommit(commit.commitId),
      ]);
      setDiffs(diffResult);
      setCanReview(canReviewResult);
    } catch (err: any) {
      console.error("Failed to load diff:", err);
    } finally {
      setLoadingDiff(false);
    }
  }

  async function handleApprove() {
    if (!selectedCommit || !session) return;
    await execute(`approve-${selectedCommit.commitId}`, () =>
      approveCommit(selectedCommit.commitId, session.username, reviewComment)
    );
    setSelectedCommit(null);
    setReviewComment("");
  }

  async function handleRequestChanges() {
    if (!selectedCommit || !session || !reviewComment) return;
    await execute(`reject-${selectedCommit.commitId}`, () =>
      requestChanges(selectedCommit.commitId, session.username, reviewComment)
    );
    setSelectedCommit(null);
    setReviewComment("");
  }

  const commits = tab === "pending" ? pending.data || [] : reviewed.data || [];

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2>Code Review</h2>
            <p>Review commits before they can be added to a version package.</p>
          </div>
          {pending.lastUpdated && (
            <span className="live-indicator">
              <span className="live-dot" />
              {pending.lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {actionError && (
        <div className="error-banner">⚠️ {actionError} <button onClick={clearError}>✕</button></div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        <button
          className={`btn ${tab === "pending" ? "btn-primary" : ""}`}
          style={tab !== "pending" ? { background: "var(--card-bg)", border: "1px solid var(--border)" } : {}}
          onClick={() => setTab("pending")}
        >
          Pending Review ({pending.data?.length || 0})
        </button>
        <button
          className={`btn ${tab === "reviewed" ? "btn-primary" : ""}`}
          style={tab !== "reviewed" ? { background: "var(--card-bg)", border: "1px solid var(--border)" } : {}}
          onClick={() => setTab("reviewed")}
        >
          Reviewed
        </button>
      </div>

      {/* Commit list */}
      <div className="card">
        {commits.length === 0 ? (
          <p style={{ padding: "1.5rem", color: "var(--text-muted)", textAlign: "center" }}>
            {tab === "pending" ? "No commits pending review." : "No reviewed commits yet."}
          </p>
        ) : (
          <div className="review-list">
            {commits.map((commit, idx) => (
              <div
                key={commit.commitId}
                className={`review-item ${selectedCommit?.commitId === commit.commitId ? "selected" : ""} animate-fade-in`}
                onClick={() => handleSelectCommit(commit)}
              >
                <div className="ri-left">
                  <span className="ri-number">#{idx + 1}</span>
                  <div>
                    <div className="ri-message">{commit.message.split("\n")[0]}</div>
                    <div className="ri-meta">
                      <code>{commit.commitId.substring(0, 7)}</code>
                      <span>by {commit.author}</span>
                      <span>{new Date(commit.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="ri-right">
                  <span className={`badge ${reviewStatusColors[commit.reviewStatus || "pending"]}`}>
                    {commit.reviewStatus || "pending"}
                  </span>
                  {commit.reviewedBy && (
                    <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                      by {commit.reviewedBy}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diff viewer */}
      {selectedCommit && (
        <div className="card animate-slide-up" style={{ marginTop: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <h3 style={{ fontSize: "1rem" }}>{selectedCommit.message.split("\n")[0]}</h3>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                <code>{selectedCommit.commitId.substring(0, 12)}</code> by {selectedCommit.author}
              </span>
            </div>
            <button className="btn" onClick={() => setSelectedCommit(null)}>✕ Close</button>
          </div>

          {loadingDiff ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
              Loading diff...
            </div>
          ) : diffs.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No file changes found.</p>
          ) : (
            <div className="diff-viewer">
              {diffs.map((file) => (
                <div key={file.filePath} className="diff-file">
                  <div className="diff-file-header">
                    <span className={`diff-change-type ${file.changeType === "A" ? "added" : file.changeType === "D" ? "deleted" : "modified"}`}>
                      {file.changeType === "A" ? "+" : file.changeType === "D" ? "-" : "M"}
                    </span>
                    <span className="diff-file-path">{file.filePath}</span>
                  </div>
                  <div className="diff-hunks">
                    {file.hunks.map((hunk, hIdx) => (
                      <div key={hIdx} className="diff-hunk">
                        <div className="diff-hunk-header">
                          @@ -{hunk.oldStart},{hunk.oldLines} +{hunk.newStart},{hunk.newLines} @@
                        </div>
                        {hunk.lines.map((line, lIdx) => (
                          <div key={lIdx} className={`diff-line diff-${line.type}`}>
                            <span className="diff-line-num">
                              {line.type !== "add" ? line.oldLineNumber : ""}
                            </span>
                            <span className="diff-line-num">
                              {line.type !== "remove" ? line.newLineNumber : ""}
                            </span>
                            <span className="diff-line-prefix">
                              {line.type === "add" ? "+" : line.type === "remove" ? "-" : " "}
                            </span>
                            <span className="diff-line-content">{line.content}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Review actions */}
          {can("reviews:approve") && (selectedCommit.reviewStatus === "pending" || !selectedCommit.reviewStatus) && (
            <div className="review-actions" style={{ marginTop: "1.5rem", borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
              {!canReview ? (
                <div className="review-blocked">
                  🔒 Previous commits must be approved before this one can be reviewed.
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Review Comment</label>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Optional for approval, required for requesting changes..."
                      rows={3}
                      style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid var(--border)", resize: "vertical", fontFamily: "inherit", fontSize: "0.85rem" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "0.75rem" }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleApprove}
                      disabled={busy !== null}
                    >
                      ✓ Approve
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={handleRequestChanges}
                      disabled={busy !== null || !reviewComment}
                      title={!reviewComment ? "Comment required when requesting changes" : ""}
                    >
                      ✕ Request Changes
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Show existing review */}
          {selectedCommit.reviewedBy && selectedCommit.reviewComment && (
            <div style={{ marginTop: "1rem", padding: "0.75rem", background: "var(--bg)", borderRadius: "6px" }}>
              <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>
                Review by {selectedCommit.reviewedBy} · {selectedCommit.reviewedAt ? new Date(selectedCommit.reviewedAt).toLocaleString() : ""}
              </div>
              <div style={{ fontSize: "0.85rem" }}>{selectedCommit.reviewComment}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
